import { Localization } from "../Localization.class";
import { IPostsApiResponse } from "models/post";
import { BaseSeoAdapter } from "./BaseSeoAdapter";
import EssentialData from "../../../../private/classes/EssentialData.class";
import SeoItemModel, {
  EnumSeoItemLevel,
  EnumSeoItemType,
  factoryCustomLandingSeoItem,
  factoryDefaultCpsSeoItem,
  factoryNeighbourhoodSeoItem,
  findItemCountry,
  findSeoItem,
  findShopCateogry,
  generateBreadcrumbs,
  generateShopsBreadcrumbs, getAutosLandingPageFAQ,
  getHrefLangs, getItemShop,
  getParsedRequestData
} from "../../models/SeoItemModel";
import { ILiteRouterApiResponse } from "models/ILiteRouterApiResponse";
import { ICoreModels, ISeoCache, ISeoPage, ITypeBreadcrumb } from "../../types/types";
import { ICountryAsset } from "models/Assets/ICountryAsset";
import { getNeighbourhoodDescriptionByParams } from "../../models/NeighbourhoodDescriptionModel";
import LandingApiResponse from "models/LandingApiModels";
import { imageHelper, labelHelper, trField, urlHelper } from "helpers/themeHelpers";
import { getCoreModels } from "../../helpers/getCoreModels";
import { ICategoryAsset } from "models/Assets/ICategoryAsset";
import { EnumScreenNames } from "../../../../enums/EnumScreenNames";
import { breadcrumbGenerate, generateFAQSchemaOrg, getCustomIndexStrategy } from "../../helpers/initSeoData";
import { generateBreadcrumbsSchemaOrg } from "libs/seo-widget/helpers/initSeoData";
import { isCategoryRealEstate } from "helpers/osHelpers";
import { IMeta } from "models/LandingApiModels/ICommon";
import { TypeAllowedCustomLandingBaseNames } from "componentScreens/LandingPages";
import {
  IMembershipInfoResponse,
  IMidAds,
  IShopQueryString
} from "../../../../screens/AccountMembership/_private/types/midView";
import {getCityDescription} from "../../models/SeoItemCityDescription";

export class SeoItemAdapter extends BaseSeoAdapter {
  private localization: Localization;

  constructor(
    private readonly seoCache: ISeoCache,
    private readonly country: ICountryAsset,
    essentialData: EssentialData
  ) {
    super(essentialData);
  }

  setLocalization(localization: Localization): void {
    this.localization = localization;
  }

  getHome(routerApiResponse?: ILiteRouterApiResponse): {
    seoPage: ISeoPage;
    seoItemModel: SeoItemModel;
  } {
    const { facebook_url, twitter_url, instagram_url, capital_city_id } =
      this.country;
    const capitalCity = this.essentialData.getCapitalCityById(capital_city_id);
    const homeSchemaOrgLocalBusiness = [
      {
        "@context": "http://schema.org",
        "@type": "LocalBusiness",
        name: labelHelper(
          { label_ar: "السوق المفتوح", label_en: "OpenSooq" },
          this.localization.lang
        ),
        description: labelHelper(
          {
            label_en:
              "OpenSooq is the largest Arabic classifieds site. You can sell and provide products and services to millions of users online without a middleman. Buyers can view goods and services such as cars, real estate, electronics, mobiles, furniture, jobs ... etc.",
            label_ar:
              "السوق المفتوح أكبر موقع للإعلانات المبوبة بالعربية. يمكنك البيع وتقديم المنتجات والخدمات لملايين المستخدمين عبر الإنترنت دون وسيط. يمكن للمشترين استعراض السلع والخدمات مثل السيارات والعقارات والإلكترونيات والموبايلات والأثاث و الوظائف ... إلخ",
          },
          this.localization.lang
        ),
        telephone: "+962 7 9018 0000",
        foundingDate: "2012",
        url: "https://__host_name_full__/ar",
        logo: process.env.NEXT_PUBLIC_CUSTOM_THEME === "shobiddak" ? process.env.NEXT_PUBLIC_PUBLIC_FOLDER_CDN +"/images/shobiddakLogo/shobiddak-144x144.png": "https://opensooqui2.os-cdn.com/OpenSooq-Logo.png",
        image: process.env.NEXT_PUBLIC_CUSTOM_THEME === "shobiddak" ? process.env.NEXT_PUBLIC_PUBLIC_FOLDER_CDN +"/images/shobiddakLogo/shobiddak-144x144.png": "https://opensooqui2.os-cdn.com/OpenSooq-Logo.png",
        address: {
          "@type": "PostalAddress",
          streetAddress: labelHelper(
            this.country?.contact_info?.location,
            this.localization.lang
          ),
          addressLocality: `${labelHelper(
            this.country,
            this.localization.lang
          )} - ${labelHelper(capitalCity, this.localization.lang)}`,
          addressCountry: labelHelper(this.country, this.localization.lang),
        },
        sameAs: [
          facebook_url,
          twitter_url,
          "https://www.youtube.com/opensooq",
          "https://www.linkedin.com/company/opensooq",
          instagram_url,
        ],
      },
    ];
    const { city } = getCoreModels(routerApiResponse, this.essentialData);
    let seoItemModel = findItemCountry(this.seoCache, this.country, city);
    const breadcrumbs = [
      {
        label: labelHelper(this.country, this.localization.lang),
        url: `/${this.localization.lang}`,
      },
      city
        ? {
            label: labelHelper(city, this.localization.lang),
            url: `/${this.localization.lang}/${urlHelper(
              city,
              this.localization.lang
            )}`,
          }
        : ({} as ITypeBreadcrumb),
    ].filter((val) => labelHelper(val, this.localization.lang));

    const hrefLangs = getHrefLangs(
      this.essentialData,
      this.localization,
      this.country,
      city
    );

    const seoPage: ISeoPage = {
      pageTitle:
        trField(seoItemModel, "page_title", this.localization.lang) ?? null,
      pageH1:
        trField(seoItemModel, "page_h1_title", this.localization.lang) ?? null,
      pageMetaDescription:
        trField(
          seoItemModel,
          "page_meta_description",
          this.localization.lang
        ) ?? null,
      pageArticle:
        trField(seoItemModel, "page_description", this.localization.lang) ??
        null,
      link2:
        "/" +
        [`__lang2__`, urlHelper(city, this.localization.lang2)]
          .filter(Boolean)
          .join("/"),
      breadcrumbs,
      ...(breadcrumbs.length == 0
        ? { "schema.org": [JSON.stringify(homeSchemaOrgLocalBusiness)] }
        : {
            "schema.org": [
              generateBreadcrumbsSchemaOrg([breadcrumbs[0]]),
              JSON.stringify(homeSchemaOrgLocalBusiness),
            ],
          }),
      hrefLangs,
    };

    seoPage["schema.org"].push(
      JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        url:
          "https://" +
          this.country.abbreviation +
          ".opensooq.com/" +
          this.localization.lang,
        potentialAction: {
          "@type": "SearchAction",
          target:
            "https://" +
            this.country.abbreviation +
            ".opensooq.com/" +
            this.localization.lang +
            "/find?PostSearch[term]={search_term_string}",
          "query-input": "required name=search_term_string",
        },
      })
    );

    return { seoPage, seoItemModel };
  }
  private generateShopDetailBreadcrumbs = (
      country: ICountryAsset,
      category: ICategoryAsset,
      shopInfo: IMembershipInfoResponse,
      localization: Localization,
  ): ITypeBreadcrumb[] => {
    const breadcrumbs: ITypeBreadcrumb[] = [];
    breadcrumbs.push({
      label: labelHelper(country, localization.lang),
      url: `/${localization.lang}`,
      url2: `/${localization.lang2}`,
    });
    const linkAr = `/ar/متاجر`;
    const linkEn = `/en/shops`;
    breadcrumbs.push({
      label: localization.trPlaceholder(
          EnumScreenNames.SHOPS_LIST_SCREEN,
          "shops"
      ),
      url: localization.lang == "ar" ? linkAr : linkEn,
      url2: localization.lang != "ar" ? linkAr : linkEn,
    });
    if (category) {
      breadcrumbs.push({
        label: labelHelper(category, localization.lang),
        url:
            breadcrumbs[breadcrumbs.length - 1].url +
            "/" +
            urlHelper(category, localization.lang),
        url2:
            breadcrumbs[breadcrumbs.length - 1].url2 +
            "/" +
            urlHelper(category, localization.lang2),
      });
    }
    const relativeShopLink =
      "/" +
      shopInfo.member?.branding?.share?.link?.split("/")?.slice(-3)?.join("/");
    breadcrumbs.push({
      label: shopInfo.member?.branding?.name,
      url: relativeShopLink ?? "",
    });

    return breadcrumbs;
  };
  getShop({screenKey, memberInfo, extendedQuery ,totalAdsCount}): {
    screenKey: string;
    memberInfo: IMembershipInfoResponse;
    extendedQuery: IShopQueryString;
    totalAdsCount: number;
  } {
    let seoItemModel = getItemShop(this.seoCache, this.country);
      let data: ISeoPage = this.localization.getJson(screenKey);
      const link2 = this.localization.trSeoField(screenKey, "link2", {
        url_name: memberInfo?.member?.branding?.share?.link?.split("/").slice(-1)[0],
      });
      const fillShopPlaceholders=(str:string)=>{
        return str?.replace("__shop_name__", memberInfo?.member?.branding?.name || "")
            ?.replace("__category__", memberInfo?.member?.branding?.category_name || "")
            ?.replace("__city__", memberInfo?.member?.branding?.city_name || "")
            ?.replace("__ads_number__", totalAdsCount || "")
      }
    data = {
      ...data,
      pageTitle: fillShopPlaceholders(
        trField(seoItemModel, "page_title", this.localization.lang)
      ),
      pageMetaDescription: fillShopPlaceholders(
        trField(seoItemModel, "page_meta_description", this.localization.lang)
      ),
      link2: link2 + (extendedQuery ? `?info=${extendedQuery}` : ""),
      canonical: memberInfo.member?.branding?.link,
      breadcrumbs: this.generateShopDetailBreadcrumbs(
        this.country,
        this.essentialData.getMainCategoryById(
          memberInfo?.member?.branding?.category_id
        ) ?? null,
        memberInfo,
        this.localization
      ),
    };
      return data as any;
  }

  getCustomLandingPage(
    routerApiResponse: ILiteRouterApiResponse,
    baseName: TypeAllowedCustomLandingBaseNames
  ): { seoItemModel: SeoItemModel; seoPage: ISeoPage } {
    const category = this.essentialData.getCategoryByCustomBaseName(baseName);

    let { city, neighborhood } = getCoreModels(
      routerApiResponse,
      this.essentialData
    );
    neighborhood = isCategoryRealEstate(category) ? neighborhood : null;

    const seoItemModel = factoryCustomLandingSeoItem(category);

    const hrefLangs = getHrefLangs(
      this.essentialData,
      this.localization,
      this.country,
      city,
      neighborhood,
      category
    );
    const breadcrumbs = generateBreadcrumbs(
      this.country,
      category,
      null,
      null,
      city,
      neighborhood,
      this.localization
    );
    const canonical = breadcrumbs[breadcrumbs.length - 1].url;

    const seoPage: ISeoPage = {
      pageTitle:
        trField(seoItemModel, "page_title", this.localization.lang) ?? null,
      pageH1:
        trField(seoItemModel, "page_h1_title", this.localization.lang) ?? null,
      pageMetaDescription:
        trField(
          seoItemModel,
          "page_meta_description",
          this.localization.lang
        ) ?? null,
      pageArticle:
        trField(seoItemModel, "page_description", this.localization.lang) ??
        null,
      canonical,
      link2: breadcrumbs[breadcrumbs.length - 1].url2,
      breadcrumbs,
      hrefLangs,
      "schema.org": [generateBreadcrumbsSchemaOrg(breadcrumbs, true)],
    };
    return { seoItemModel, seoPage };
  }

  getLandingPage(
    routerApiResponse: ILiteRouterApiResponse,
    landingApiResponse: LandingApiResponse
  ): { seoItemModel: SeoItemModel; seoPage: ISeoPage } {
    let { category, subCategory, city, neighborhood } = getCoreModels(
      routerApiResponse,
      this.essentialData
    );
    neighborhood = isCategoryRealEstate(category) ? neighborhood : null;

    let response = getParsedRequestData(
      this.seoCache,
      this.country,
      city,
      neighborhood,
      category,
      subCategory
    );

    const hrefLangs = getHrefLangs(
      this.essentialData,
      this.localization,
      this.country,
      city,
      neighborhood,
      category
    );
    const { type, level, model_id, parent_model_id } = response;
    let seoItemModel = findSeoItem(
      this.seoCache,
      this.country,
      type,
      model_id,
      level,
      parent_model_id
    );

    const cityDescription = (routerApiResponse?.cityId)? getCityDescription(
        this.seoCache,
        seoItemModel.id,
        routerApiResponse.cityId
    ) : null;

    let pageArticle = "";
    if (cityDescription) {
      pageArticle = trField(
          cityDescription,
          "page_description",
          this.localization.lang
      );
    } else if (seoItemModel) {
      pageArticle = trField(seoItemModel, "page_description", this.localization.lang);
    }
    const breadcrumbs = generateBreadcrumbs(
      this.country,
      category,
      subCategory,
      null,
      city,
      neighborhood,
      this.localization
    );
    const seoPage: ISeoPage = {
      pageTitle:
        trField(seoItemModel, "page_title", this.localization.lang) ?? null,
      pageH1:
        trField(seoItemModel, "page_h1_title", this.localization.lang) ?? null,
      canonical: "__url__",
      pageMetaDescription:
        trField(
          seoItemModel,
          "page_meta_description",
          this.localization.lang
        ) ?? null,
      pageArticle,
      link2: breadcrumbs[breadcrumbs.length - 1].url2,
      breadcrumbs,
      hrefLangs,
      "schema.org": [
        generateBreadcrumbsSchemaOrg(breadcrumbs, true),
        category.reporting_name == "Autos"
          ? generateFAQSchemaOrg(getAutosLandingPageFAQ(this.localization))
          : null,
      ],
      FQA:
        category.reporting_name == "Autos"
          ? getAutosLandingPageFAQ(this.localization)
          : null,
    };
    return { seoItemModel, seoPage };
  }

  getSerpData(
    routerApiResponse: ILiteRouterApiResponse,
    page: number,
    postResponse?: IPostsApiResponse,
    serpType?: string
  ): { seoPage: ISeoPage; seoItemModel: SeoItemModel } {
    let { category, subCategory, city, neighborhood, cpOptions } =
      getCoreModels(routerApiResponse, this.essentialData);
    neighborhood =
      !category || isCategoryRealEstate(category) ? neighborhood : null;
    const { type, level, model_id, parent_model_id } = getParsedRequestData(
      this.seoCache,
      this.country,
      city,
      neighborhood,
      category,
      subCategory,
      cpOptions
    );

    let faqs = null;

    const fqaScreenKey = "Other";
    if ((level === EnumSeoItemLevel.LEVEL_CATEGORY || level === EnumSeoItemLevel.LEVEL_SUBCATEGORY) && !isCategoryRealEstate(category)) {
      const reporting = level === EnumSeoItemLevel.LEVEL_CATEGORY ? category.reporting_name : subCategory.reporting_name;
      faqs = level === EnumSeoItemLevel.LEVEL_CATEGORY ? this.getFQACategory(postResponse, {
        category,
        subCategory,
        city,
        neighborhood,
        cpOptions: Array.isArray(cpOptions) ? cpOptions : []
      }) : this.getFQASubCategory(postResponse, {
        category,
        subCategory,
        city,
        neighborhood,
        cpOptions: Array.isArray(cpOptions) ? cpOptions : []
      });
    }
    if ((level === EnumSeoItemLevel.LEVEL_CATEGORY || level === EnumSeoItemLevel.LEVEL_SUBCATEGORY)
      && isCategoryRealEstate(category)) {
      faqs = this.getFQARealEstate(postResponse, {
        category,
        subCategory,
        city,
        neighborhood,
        cpOptions: Array.isArray(cpOptions) ? cpOptions : []
      });
    }
    if (level === EnumSeoItemLevel.LEVEL_PARAM1 && !isCategoryRealEstate(category)) {
      switch (subCategory.reporting_name) {
        case "CarsForRent":
        case "CarsForSale":
        case "TrucksTrailers":
        case "Shoes":
        case "RefrigeratorsAndFreezers":
        case "WashingMachinesAndDryers":
        case "CamerasPhotography":
        case "Ovens":
        case "Dishwashers":
        case "TVs":
        case "MensShoes":
        case "Tablets":
        case "MobilePhones":
        case "MotorCyclesForSale":
          faqs = [...this.getFQACp1(postResponse, {
            category,
            subCategory,
            city,
            neighborhood,
            cpOptions: Array.isArray(cpOptions) ? cpOptions : []
          }, fqaScreenKey),...this.getFQACp1(postResponse, {
            category,
            subCategory,
            city,
            neighborhood,
            cpOptions: Array.isArray(cpOptions) ? cpOptions : []
          }, subCategory.reporting_name,1,false)];
          break;
        default:
          faqs = this.getFQACp1(postResponse, {
            category,
            subCategory,
            city,
            neighborhood,
            cpOptions: Array.isArray(cpOptions) ? cpOptions : []
          }, fqaScreenKey);
      }
    }


    let seoItemModel: SeoItemModel;
    if (type === EnumSeoItemType.TYPE_DEFAULT_CP) {
      seoItemModel = factoryDefaultCpsSeoItem();
    } else if (type === EnumSeoItemType.TYPE_NEIGHBORHOOD) {
      seoItemModel = factoryNeighbourhoodSeoItem(neighborhood);
    } else {
      seoItemModel = findSeoItem(
        this.seoCache,
        this.country,
        type,
        model_id,
        level,
        parent_model_id
      );
    }

    const cityDescription = (routerApiResponse?.cityId)? getCityDescription(
        this.seoCache,
        seoItemModel.id,
        routerApiResponse.cityId
    ) : null;

    const neighbourhoodDescription = getNeighbourhoodDescriptionByParams(
      this.seoCache,
      routerApiResponse,
      this.country
    );

    let pageArticle = "";
    if (cityDescription) {
      pageArticle = trField(
          cityDescription,
          "page_description",
          this.localization.lang
      );
    } else if (neighbourhoodDescription) {
      pageArticle = trField(
          neighbourhoodDescription,
          "description",
          this.localization.lang
      );
    } else {
      pageArticle = trField(seoItemModel, "page_description", this.localization.lang);
    }

    const breadcrumbs = generateBreadcrumbs(
      this.country,
      category,
      subCategory,
      cpOptions,
      city,
      neighborhood,
      this.localization
    );

    const hrefLangs = getHrefLangs(
      this.essentialData,
      this.localization,
      this.country,
      city,
      neighborhood,
      category,
      subCategory,
      cpOptions
    );
    let categoryId = category?.id;
    let subId: string | number = 0;

    if (category?.parent_id) {
      subId = categoryId;
      categoryId = this.essentialData.getMainCategoryById(
        category.parent_id
      ).id;
    }
    let cityId = city?.id ?? null;
    let canonical =
      breadcrumbs[breadcrumbs.length - 1].url +
      (category && !subCategory ? "/all" : "") +
      (page > 1 ? `?page=${page}` : "");

    let pageH1: string = trField(
      seoItemModel,
      "page_h1_title",
      this.localization.lang
    );
    let pageMetaDescription = trField(
      seoItemModel,
      "page_meta_description",
      this.localization.lang
    );
    let pageTitle = trField(seoItemModel, "page_title", this.localization.lang);
    if (isCategoryRealEstate(category)) {
      if (!neighborhood) {
        pageH1 = pageH1.replace(
          "__market_name__",
          "__market_name__ __neighborhood__"
        );
        pageMetaDescription = pageMetaDescription.replace(
          "__market_name__",
          "__market_name__ __neighborhood__"
        );
        pageTitle = pageTitle.replace(
          "__market_name__",
          "__market_name__ __neighborhood__"
        );
      }
    }

    if ( serpType == 'search-no-seo' ) {
      pageH1 = labelHelper(subCategory??category, this.localization.lang);
    }
    const seoPage: ISeoPage = {
      pageTitle: pageTitle,
      pageH1: pageH1,
      pageMetaDescription: pageMetaDescription,
      pageArticle,
      breadcrumbs,
      hrefLangs: postResponse?._meta?.currentPage == 1 ? hrefLangs : null,
      "schema.org": [
        generateBreadcrumbsSchemaOrg(breadcrumbs, true),
        this.generateListItemsSchemaOrg(
          postResponse,
          pageH1,
          pageMetaDescription
        ),
        [...(faqs ? [generateFAQSchemaOrg(faqs)] : [])],
      ],
      FQA: faqs,
      link2:
        breadcrumbs[breadcrumbs.length - 1].url2 +
        (category && !subCategory ? "/all" : ""),
      canonical,
      robots: getCustomIndexStrategy(EnumScreenNames.SERP_SCREEN, {
        postsCount: postResponse?.items?.length,
        isFindPage: false,
        CPCount: cpOptions?.length || 0,
        isWithCity: !(city == undefined),
        serpType:serpType,
        subCategory
      }),
    };
    return { seoPage, seoItemModel };
  }

  getShopListingWithCategory(
    category: ICategoryAsset,
    shopsApiResponse: IMidAds
  ): {
    seoPage: ISeoPage;
    seoItemModel: SeoItemModel;
  } {
    const page = shopsApiResponse?._meta?.currentPage || 1;
    const seoItemModel = findShopCateogry(
      this.seoCache,
      this.country,
      category
    );
    const linkAr =
      `https://__country_abb__.__host_name__/ar/متاجر/${urlHelper(
        category,
        "ar"
      )}` + (page > 1 ? `?page=${page}` : "");
    const linkEn =
      `https://__country_abb__.__host_name__/en/shops/${urlHelper(
        category,
        "en"
      )}` + (page > 1 ? `?page=${page}` : "");
    const seoPage: ISeoPage = {
      canonical: this.localization.lang === "ar" ? linkAr : linkEn,
      link2: this.localization.lang === "ar" ? linkEn : linkAr,
      pageTitle:
        trField(seoItemModel, "page_title", this.localization.lang) ?? null,
      pageH1:
        trField(seoItemModel, "page_h1_title", this.localization.lang) ?? null,
      pageMetaDescription:
        trField(
          seoItemModel,
          "page_meta_description",
          this.localization.lang
        ) ?? null,
      pageArticle:
        trField(seoItemModel, "page_description", this.localization.lang) ??
        null,
      breadcrumbs: generateShopsBreadcrumbs(
        this.country,
        category,
        this.localization
      ),
    };
    return { seoPage, seoItemModel };
  }

  getRealEstatePrices(
    screenKey: EnumScreenNames,
    routerApiResponse: ILiteRouterApiResponse,
    level: EnumSeoItemLevel
  ): { seoPage: ISeoPage; seoItemModel: SeoItemModel } {
    const { subCategory, city, neighborhood } = getCoreModels(
      routerApiResponse,
      this.essentialData
    );
    const firstCrumb = {
      url_name_en: this.localization.trPlaceholder(
        screenKey,
        "base_url",
        null,
        "en"
      ),
      url_name_ar: this.localization.trPlaceholder(
        screenKey,
        "base_url",
        null,
        "ar"
      ),
      label: this.localization.trPlaceholder(screenKey, "page_name"),
    };
    const breadcrumbs = breadcrumbGenerate(
      [firstCrumb, subCategory, city, neighborhood],
      this.localization.lang
    );
    let seoItemModel = findSeoItem(
      this.seoCache,
      this.country,
      EnumSeoItemType.TYPE_REAL_ESTATE_PRICES,
      level == EnumSeoItemLevel.LEVEL_REAL_ESTATE_PRICES_LANDING
        ? this.country.id
        : this.country.id * 10,
      level
    );
    const seoPage: ISeoPage = {
      pageTitle:
        trField(seoItemModel, "page_title", this.localization.lang) || null,
      pageH1:
        trField(seoItemModel, "page_h1_title", this.localization.lang) || null,
      pageMetaDescription:
        trField(
          seoItemModel,
          "page_meta_description",
          this.localization.lang
        ) || null,
      pageArticle:
        trField(seoItemModel, "page_description", this.localization.lang) ||
        null,
      link2: breadcrumbs[breadcrumbs.length - 1].url2,
      canonical: breadcrumbs[breadcrumbs.length - 1].url,
      robots: getCustomIndexStrategy(
        EnumScreenNames.REAL_ESTATE_PRICES_SCREEN,
        { isNeighborhoodSelected: Boolean(neighborhood) }
      ),
    };
    return { seoPage, seoItemModel };
  }

  private setRealEstateFAQ(
    screenKey: EnumScreenNames,
    questionName: string,
    questionNum: number,
    selectedObjects: ICoreModels,
    meta: IMeta
  ): string {
    if (!meta) return "";

    return this.localization.trPlaceholder(
      screenKey,
      questionName + "_" + questionNum,
      {
        city: labelHelper(selectedObjects?.city, this.localization.lang),
        neighborhood: labelHelper(
          selectedObjects.neighborhood,
          this.localization.lang
        ),
        ads_number: meta.totalCount,
        shops_number: meta.FQA
          ? meta.FQA.shops_number
          : Math.round(meta.totalCount * 0.01),
        premium_ad_number: meta.FQA
          ? meta.FQA.premium_ad_number
          : Math.round(meta.totalCount / 100),
        monthly_installment_number: meta.FQA
          ? meta.FQA.monthly_installment_number
          : Math.round(meta.totalCount / 4),
      },
      this.localization.lang
    );
  }
  private setCategoryFAQ(
    screenKey: EnumScreenNames,
    questionName: string,
    questionNum: number,
    selectedObjects: ICoreModels,
    meta: IMeta
  ): string {
    if (!meta) return "";

    return this.localization.trPlaceholder(
      screenKey,
      questionName + "_" + questionNum,
      {
        city: labelHelper(selectedObjects?.city, this.localization.lang),
        neighborhood: labelHelper(
          selectedObjects.neighborhood,
          this.localization.lang
        ),
        ads_number: meta.totalCount,
      },
      this.localization.lang
    );
  }

  private setCp1FAQ(
    screenKey: EnumScreenNames,
    questionName: string,
    questionNum: number,
    selectedObjects: ICoreModels,
    meta: IMeta
  ): string {
    if (!meta) return "";
    return this.localization.trPlaceholder(
      screenKey,
      questionName + "_" + questionNum,
      {
        city: labelHelper(selectedObjects?.city, this.localization.lang),
        neighborhood: labelHelper(
          selectedObjects.neighborhood,
          this.localization.lang
        ),
        ads_number: meta.totalCount,
        __model1__: meta.FQA?.__model1__
          ? labelHelper(meta.FQA.__model1__,this.localization.lang):'',
        __model2__: meta.FQA?.__model2__
          ? ", "+labelHelper(meta.FQA.__model2__,this.localization.lang):'',
        __model3__: meta.FQA?.__model3__
          ? ", "+labelHelper(meta.FQA.__model3__,this.localization.lang):''
      },
      this.localization.lang
    );
  }

  private getFQARealEstate(
    postApiResponse: IPostsApiResponse,
    selectedObjects: ICoreModels
  ) {
    const { category } = selectedObjects;
    let fqaRealEstate = [];
    this.range(1, 4)?.forEach((number) => {
      fqaRealEstate.push({
        question: this.setRealEstateFAQ(
          EnumScreenNames.SERP_SCREEN,
          category.reporting_name + "_q",
          number,
          selectedObjects,
          postApiResponse?._meta
        ),
        answer: this.setRealEstateFAQ(
          EnumScreenNames.SERP_SCREEN,
          category.reporting_name + "_a",
          number,
          selectedObjects,
          postApiResponse?._meta
        ),
      });
    });

    return fqaRealEstate;
  }

  private getFQACategory(
    postApiResponse: IPostsApiResponse,
    selectedObjects: ICoreModels
  ) {
    const { category } = selectedObjects;
    let fqaCategory = [];
    this.range(1, 4)?.forEach((number) => {
      fqaCategory.push({
        question: this.setCategoryFAQ(
          EnumScreenNames.SERP_SCREEN,
          "OtherCategory_q",
          number,
          selectedObjects,
          postApiResponse?._meta
        ),
        answer: this.setCategoryFAQ(
          EnumScreenNames.SERP_SCREEN,
          "OtherCategory_a",
          number,
          selectedObjects,
          postApiResponse?._meta
        ),
      });
    });

    return fqaCategory;
  }
  private getFQASubCategory(
    postApiResponse: IPostsApiResponse,
    selectedObjects: ICoreModels
  ) {
    const { category } = selectedObjects;
    let fqaCategory = [];
    this.range(1, 4)?.forEach((number) => {
      fqaCategory.push({
        question: this.setCategoryFAQ(
          EnumScreenNames.SERP_SCREEN,
          "OtherSubCategory_q",
          number,
          selectedObjects,
          postApiResponse?._meta
        ),
        answer: this.setCategoryFAQ(
          EnumScreenNames.SERP_SCREEN,
          "OtherSubCategory_a",
          number,
          selectedObjects,
          postApiResponse?._meta
        ),
      });
    });

    return fqaCategory;
  }

  private getFQACp1(
    postApiResponse: IPostsApiResponse,
    selectedObjects: ICoreModels,
    reportingName: string,
    size: number = 4,
    other = true
  ) {
    const { category } = selectedObjects;
    let fqaCategory = [];
    if (!postApiResponse._meta) return fqaCategory;
    if (!other && !postApiResponse._meta.FQA?.__model1__) return fqaCategory;

    this.range(1, size)?.forEach((number) => {
      fqaCategory.push({
        question: this.setCp1FAQ(
          EnumScreenNames.SERP_SCREEN,
          `Cp1${reportingName}_q`,
          number,
          selectedObjects,
          postApiResponse?._meta
        ),
        answer: this.setCp1FAQ(
          EnumScreenNames.SERP_SCREEN,
          `Cp1${reportingName}_a`,
          number,
          selectedObjects,
          postApiResponse?._meta
        )
      });
    });

    return fqaCategory;
  }



  private range(startAt = 0, size) {
    return [...Array(size).keys()].map((i) => i + startAt);
  }

  private generateListItemsSchemaOrg(
    postResponse: IPostsApiResponse,
    pageH1: string,
    pageMetaDescription: string
  ): string {
    if (!postResponse?.items?.length) {
      return "";
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: pageH1,
      description: pageMetaDescription,
      numberOfItems: `${postResponse.items.length}`,
      itemListElement: postResponse.items.map((item, index) => {
        const priceValidUntil = item.record_expiration_date_timestamp && new Date(item.record_expiration_date_timestamp * 1000).toISOString();
        return {
          "@type": ["ListItem", "Product"],
          position: index + 1,
          url: item.post_url,
          name: item.title,
          description: item.metaDescription,
          image: imageHelper(
            item?.first_image_thumb ?? item?.first_image_uri,
            "2048x0"
          ),
          offers: {
            "@type": "Offer",
            price: `${item.price ?? 0}`,
            priceCurrency:
              Object.values(item.price_list)[0]?.price_code ??
              this.country.currency_en,
            priceValidUntil,
            availability: "http://schema.org/InStock",
          },
        };
      }),
    };

    return JSON.stringify(schema);
  }
}
